/**
 * HQ Phase-6 light rig.
 *
 * The directional KEY_SUN remains owned by scene.ts and is the sole shadow
 * caster. This module owns only bounded, shadowless interior fills and toggles
 * them by the player's current floor so upper levels do not inherit one giant
 * ground-floor point light.
 */
import * as pc from 'playcanvas'
import { worldRoot } from './worldRoot'

export type HqLightFloor = 0 | 1 | 2 | 3

/** Dark stone → warm slate → soft concrete → cool terrazzo. */
export const HQ_FLOOR_MATERIAL_COLORS = [
  [0.135, 0.150, 0.175],
  [0.30, 0.285, 0.265],
  [0.43, 0.435, 0.445],
  [0.60, 0.62, 0.64],
] as const

export const HQ_FLOOR_MATERIAL_GLOSS = [0.28, 0.22, 0.24, 0.30] as const

export interface HqLightSpec {
  id: string
  floor: HqLightFloor
  type: 'point' | 'spot'
  position: readonly [number, number, number]
  color: readonly [number, number, number]
  intensity: number
  range: number
  coneAngle?: number
}

export const HQ_LIGHT_SPECS: readonly HqLightSpec[] = [
  {
    id: 'l0-reception-key',
    floor: 0,
    type: 'spot',
    position: [0, 3.2, 12],
    color: [1, 0.78, 0.56],
    intensity: 3.2,
    range: 10,
    coneAngle: 52,
  },
  {
    id: 'l0-atrium-uplight',
    floor: 0,
    type: 'point',
    position: [4, 0.6, 0],
    color: [0.31, 0.52, 0.72],
    intensity: 1,
    range: 8,
  },
  ...([-9, 9] as const).flatMap((z, row) =>
    ([-15, -9] as const).map((x, column): HqLightSpec => ({
      id: `l1-desk-fill-${row * 2 + column + 1}`,
      floor: 1,
      type: 'point',
      position: [x, 8.3, z],
      color: [1, 0.87, 0.69],
      intensity: 1.6,
      range: 6,
    })),
  ),
  {
    id: 'l1-focus-studio-fill',
    floor: 1,
    type: 'point',
    position: [-21, 7.7, -3],
    color: [1, 0.88, 0.72],
    intensity: 2.2,
    range: 6.5,
  },
  {
    id: 'l1-team-huddle-fill',
    floor: 1,
    type: 'point',
    position: [-21, 7.7, 3],
    color: [1, 0.78, 0.58],
    intensity: 2.2,
    range: 6.5,
  },
  {
    id: 'l1-bay-graze',
    floor: 1,
    type: 'spot',
    position: [20, 8.2, 0],
    color: [1, 0.79, 0.58],
    intensity: 2,
    range: 9,
    coneAngle: 58,
  },
  {
    id: 'l2-gallery-wash-1',
    floor: 2,
    type: 'spot',
    position: [-17, 12.4, -13.5],
    color: [0.91, 0.94, 1],
    intensity: 2.2,
    range: 8,
    coneAngle: 48,
  },
  {
    id: 'l2-boardroom-fill',
    floor: 2,
    type: 'point',
    position: [9.5, 12.1, -11],
    color: [0.90, 0.94, 1],
    intensity: 1.45,
    range: 8,
  },
  {
    id: 'l2-bay-graze',
    floor: 2,
    type: 'spot',
    position: [20, 12.3, 0],
    color: [1, 0.79, 0.58],
    intensity: 2,
    range: 9,
    coneAngle: 58,
  },
  {
    id: 'l3-sky-fill-1',
    floor: 3,
    type: 'point',
    position: [-12.5, 16.6, -11],
    color: [0.95, 0.98, 1],
    intensity: 1.15,
    range: 9,
  },
  {
    id: 'l3-core-crown-fill',
    floor: 3,
    type: 'point',
    position: [4, 16.6, 9],
    color: [0.55, 0.88, 1],
    intensity: 1.6,
    range: 8,
  },
]

export const HQ_LIGHT_COUNTS = [0, 1, 2, 3].map((floor) =>
  HQ_LIGHT_SPECS.filter((spec) => spec.floor === floor).length)

export function hqFloorForHeight(y: number): HqLightFloor {
  if (!Number.isFinite(y) || y < 2.25) return 0
  if (y < 6.75) return 1
  if (y < 11.25) return 2
  return 3
}

export function enabledHqFloors(
  y: number,
  insideHq: boolean,
): ReadonlySet<HqLightFloor> {
  if (!insideHq) return new Set()
  const floor = hqFloorForHeight(y)
  return new Set(
    ([floor - 1, floor, floor + 1] as number[])
      .filter((value): value is HqLightFloor => value >= 0 && value <= 3),
  )
}

export interface HqLightingController {
  readonly lights: readonly pc.Entity[]
  update(playerY: number, insideHq: boolean): void
  destroy(): void
}

export function buildHqLighting(app: pc.Application): HqLightingController {
  const root = worldRoot(app)
  const entries = HQ_LIGHT_SPECS.map((spec) => {
    const entity = new pc.Entity(`HQ_LIGHT_${spec.id}`)
    entity.addComponent('light', {
      type: spec.type,
      color: new pc.Color(...spec.color),
      intensity: spec.intensity,
      range: spec.range,
      castShadows: false,
      ...(spec.type === 'spot'
        ? {
            innerConeAngle: Math.max(0, (spec.coneAngle ?? 50) - 12),
            outerConeAngle: spec.coneAngle ?? 50,
          }
        : {}),
    })
    entity.setLocalPosition(...spec.position)
    entity.enabled = false
    root.addChild(entity)
    return { spec, entity }
  })

  let activationKey = ''
  return {
    lights: entries.map(({ entity }) => entity),
    update(playerY, insideHq) {
      const enabled = enabledHqFloors(playerY, insideHq)
      const nextKey = [...enabled].join(',')
      if (nextKey === activationKey) return
      activationKey = nextKey
      for (const { spec, entity } of entries) entity.enabled = enabled.has(spec.floor)
    },
    destroy() {
      for (const { entity } of entries) entity.destroy()
      entries.length = 0
      activationKey = ''
    },
  }
}
